import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageProvider';
import '../style/microphoneBanner.css';

/**
 * Renders a fixed red bar at the top of the page when the user's microphone has
 * not been enabled, asking them to enable it. Mirrors BrowserSupportBanner.
 *
 * Unlike the browser, mic permission can change while the page is open (the user
 * may grant/revoke it), so we watch the Permissions API and update live. When the
 * permission state can't be determined (Permissions API missing, or 'microphone'
 * unsupported) we fail open and show nothing rather than nag the user.
 */
function MicrophoneBanner() {
  const { t } = useTranslation();
  const { dir } = useLanguage();
  // null = undetermined (don't show); true = enabled; false = not enabled.
  const [micEnabled, setMicEnabled] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let permissionStatus = null;

    const applyState = () => {
      if (cancelled || !permissionStatus) return;
      setMicEnabled(permissionStatus.state === 'granted');
    };

    if (!navigator?.permissions?.query) {
      return undefined;
    }

    navigator.permissions
      .query({ name: 'microphone' })
      .then((status) => {
        if (cancelled) return;
        permissionStatus = status;
        setMicEnabled(status.state === 'granted');
        if (status.addEventListener) {
          status.addEventListener('change', applyState);
        } else {
          status.onchange = applyState;
        }
      })
      .catch(() => {
        // 'microphone' may be unsupported by the Permissions API; fail open.
      });

    return () => {
      cancelled = true;
      if (!permissionStatus) return;
      if (permissionStatus.removeEventListener) {
        permissionStatus.removeEventListener('change', applyState);
      } else {
        permissionStatus.onchange = null;
      }
    };
  }, []);

  if (micEnabled !== false) return null;

  return (
    <div className="microphone-banner" role="alert">
      {t('microphonePermission.disabled', {
        defaultValue:
          'Your microphone is not enabled. Please enable microphone access.',
      })}
    </div>
  );
}

export default MicrophoneBanner;
